import type { WorkspaceFile } from '../../../lib/workspace-files';
import { useTranslation } from '../../../lib/i18n';
import { extensionOf, humanSize, typeLabel } from '../../../lib/artifact-file';
import { formatConversationDate } from '../../../lib/conversations';
import styles from './FileDetailMeta.module.css';

type TFunc = (key: string, options?: Record<string, unknown>) => string;

// The name/kind/attribution subtitle + Kind/Size/Type/Date metadata rows — shared by BOTH the in-app
// viewer's own header (`DocViewerModal`, the mobile-web standalone file page) and the no-viewer fallback
// (`FileDetailBody`), so an artifact reads identically whichever shell hosts it (Figma node 8224-139650).
export function FileDetailMeta({ item, sizeBytes, workspaceName }: { item: WorkspaceFile; sizeBytes: number | null; workspaceName?: string }) {
  const { t } = useTranslation();

  return (
    <div>
      <h2 className={styles.title}>{item.name}</h2>
      <p className={styles.subtitle}>{fileSubtitle(item, t, workspaceName)}</p>
      <FileDetailRows item={item} sizeBytes={sizeBytes} />
    </div>
  );
}

// The subtitle text — kind (`File`/`App`), then the attribution and the workspace name, each only when the
// datum exists. The viewer's Figma header (12811-162388) reads "File · built by Melody · Summer Menu Launch";
// `builtBy` is undefined for every file until the harness write side lands, and `workspaceName` is only
// passed by the surfaces that know it, so both segments are omitted rather than fabricated.
export function fileSubtitle(item: WorkspaceFile, t: TFunc, workspaceName?: string): string {
  const parts = [item.isApp ? t('agentCreations.kind.app') : t('agentCreations.kind.file')];
  if (item.builtBy) parts.push(`${t('files.createdBy')} ${item.builtBy.name}`);
  if (workspaceName) parts.push(workspaceName);
  return parts.join(' · ');
}

/** The same facts as {@link FileDetailRows}, as one caption line — what the viewer shows under the
 *  document. The rows form stays for the file-detail page, which has room for a definition list. */
export function FileMetaLine({ item, sizeBytes }: { item: WorkspaceFile; sizeBytes: number | null }) {
  const { t } = useTranslation();
  const type = !item.isApp ? typeLabel(extensionOf(item)) : null;
  const parts = [
    item.isApp ? t('agentCreations.kind.app') : t('agentCreations.kind.file'),
    sizeBytes != null ? humanSize(sizeBytes) : null,
    type,
    item.updatedDate ? formatConversationDate(item.updatedDate) : null,
  ].filter(Boolean);
  return <p className={styles.metaLine}>{parts.join(' · ')}</p>;
}

// The Kind/Size/Type/Date rows on their own — the file-detail page renders them as a definition list.
// The viewer modal uses `FileMetaLine` instead; see its note.
export function FileDetailRows({ item, sizeBytes }: { item: WorkspaceFile; sizeBytes: number | null }) {
  const { t } = useTranslation();
  const type = !item.isApp ? typeLabel(extensionOf(item)) : null;

  return (
    <dl className={styles.meta}>
      <MetaRow label={t('agentCreations.detail.kind')} value={item.isApp ? t('agentCreations.kind.app') : t('agentCreations.kind.file')} />
      {sizeBytes != null && <MetaRow label={t('agentCreations.detail.size')} value={humanSize(sizeBytes)} />}
      {!item.isApp && type && <MetaRow label={t('agentCreations.detail.type')} value={type} />}
      {item.updatedDate && <MetaRow label={t('agentCreations.table.date')} value={formatConversationDate(item.updatedDate)} />}
    </dl>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metaRow}>
      <dt className={styles.metaLabel}>{label}</dt>
      <dd className={styles.metaValue}>{value}</dd>
    </div>
  );
}
